/**
 * Pixel-perfect preview for the AOTD-IRL Spectra 6 dithered image.
 * - No smoothing: canvas imageSmoothingEnabled = false, so the dither pattern is preserved.
 * - Integer zoom only (1×–4×) to avoid blurry resampling.
 * - Optional CSS filter to approximate reflective e-ink look.
 * - Admin-only: non-admins are redirected to the AOTD page.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { url } from "../../api";
import { apiClient } from "../../api/client";
import styled from "styled-components";
import { checkRole } from "../recipe_page/recipe_page";

const PreviewWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
`;

const CanvasContainer = styled.div<{ $sim?: boolean }>`
  /* critical: don't blur dither dots when scaling */
  image-rendering: pixelated;
  image-rendering: crisp-edges;
  ${(p) =>
    p.$sim
      ? "filter: saturate(0.85) contrast(0.95) brightness(1.03);"
      : ""}
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Label = styled.label`
  font-size: 0.9rem;
  color: #333;
`;

const IMAGE_URL = `${url}/api/aotd-irl/image?test=1`;

const AotdIrlEpdPreviewInner: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [zoom, setZoom] = useState(1);
  const [sim, setSim] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const applyZoom = useCallback((z: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.style.width = `${canvas.width * z}px`;
    canvas.style.height = `${canvas.height * z}px`;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.imageSmoothingEnabled = false;

    let objectUrl: string | null = null;

    apiClient
      .get(IMAGE_URL, { responseType: "blob" })
      .then((res) => {
        objectUrl = URL.createObjectURL(res.data);
        const img = new Image();
        img.onload = () => {
          setError(null);
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          ctx!.imageSmoothingEnabled = false;
          ctx!.drawImage(img, 0, 0);
          applyZoom(zoom);
          setLoading(false);
        };
        img.onerror = () => {
          setError("Failed to load image");
          setLoading(false);
        };
        img.src = objectUrl;
      })
      .catch(() => {
        setError("Failed to load image");
        setLoading(false);
      });

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [applyZoom, zoom]);

  useEffect(() => {
    applyZoom(zoom);
  }, [zoom, applyZoom]);

  return (
    <PreviewWrap>
      <h2 style={{ margin: 0, fontSize: "1.25rem" }}>AOTD-IRL EPD preview</h2>
      <p style={{ margin: 0, fontSize: "0.85rem", color: "#666" }}>
        Spectra 6 image — integer zoom only, no smoothing
      </p>

      <Controls>
        <Label>
          Zoom (integer):{" "}
          <input
            type="range"
            min={1}
            max={4}
            step={1}
            value={zoom}
            onChange={(e) => setZoom(parseInt(e.target.value, 10))}
          />
          {zoom}×
        </Label>
        <Label>
          <input
            type="checkbox"
            checked={sim}
            onChange={(e) => setSim(e.target.checked)}
          />
          Panel sim (muted ink)
        </Label>
      </Controls>

      {loading && <span>Loading…</span>}
      {error && <span style={{ color: "crimson" }}>{error}</span>}

      <CanvasContainer $sim={sim}>
        <canvas ref={canvasRef} />
      </CanvasContainer>
    </PreviewWrap>
  );
};

const AotdIrlEpdPreview: React.FC = () => {
  const user = useSelector((state: { auth: { role: string | null } }) => state.auth);
  if (!checkRole(user.role ?? "", "admin")) {
    return <Navigate to="/art-of-the-day" replace />;
  }
  return <AotdIrlEpdPreviewInner />;
};

export default AotdIrlEpdPreview;
